import { EmailHint } from "@/components/EmailHint";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Search, User } from "lucide-react";
import { AddressInput } from "@/components/AddressInput";
import { ContactInput, matchContacts, type ContactOption } from "@/components/ContactInput";
import { composeContactName, formatContactSummary, parseContact, toContactValue } from "@shared/contact";
import { formatAddressSummary } from "@shared/address";
import { formatPhone } from "@shared/phone";
import { apiRequest } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { cn } from "@/lib/utils";
import { CELL_BOX } from "./InterviewCells";
import { saveInBackground, whenSaved } from "./saveFailure";

/**
 * s221 Lane 1, mechanism 5 — THE CONTACT CONTROL (SPEC: every cell of `kind: 'joint'`).
 *
 * 🔴 s221 Lane 1c (D-221.28 + Reade's walk of staging): the board is a working prototype and this is
 * its behaviour, not only its look. Two pieces, both from `Main.dc.html`:
 *
 *  • `LinkedPersonCell` — "ONE CONTACT TRIGGER, PRODUCT-WIDE": a 44px box, a person (or building) mark,
 *    the name(s) or the cell's `word`, ellipsis on overflow. Clicking it opens the card UNDER THE ROW.
 *  • `ContactCard` — "THE CONTACT CARD: the standard person control": the cell's `title`; a New contact /
 *    Existing contact toggle; for a new contact Name · Mobile · Email and "+ Add an address" folded shut
 *    (the product's six-field address, rule 28 / rule 63); up to three people, each after the first with
 *    Remove; for an existing contact "Search your contacts — start typing a name" with Recent pills; a
 *    footer of the `more` link, a green Done and the cell's `undo`. `org` swaps the labels to the
 *    board's company branch (Company name · Main phone · Email or website; "A person to ask for").
 *
 * WHERE THE ANSWERS GO. The card is declared by SLOTS: each person maps to fields ON THE ROW (the
 * guardian's name, phone, email and address; the doctor's name, phone, address). Every write goes
 * through the screen's one planner write path (POST generic-entries, D-210.18) — this file writes
 * nothing itself. A person slot whose name field is `contact`-typed uses ContactInput, which MINTS a
 * contact the rest of the plan can reuse and stores the LINK `{ display, ref }` (D-221.12). A slot whose
 * name is a `text` field (doctor, school) or the row's own name parts (Who) stores the words; a field the
 * row does not have is not drawn — the card never collects an answer it cannot keep.
 *
 * 🔴 s221 CONTACT-CARD-ONE-1 (Reade: "We use one contact card for everyone"). Every slot now draws the
 * same four answers — Name · Mobile · Email · "+ Add an address" — because every row carries them. And a
 * contact stops being name-only: on Done the card writes the mobile and email onto the CONTACT RECORD
 * too (the row keeps its copy — three executor tasks read the row, and repointing them is a later lane).
 * An organisation (doctor, school) keeps its name as TEXT on the row — guardrail 2: a text field stays an
 * extraction target, a contact field never is — and the card finds-or-mints the organisation contact
 * (`kind: 'organisation'`) beside it, so the practice is reusable without costing document extraction.
 * The address stays on the row for everyone (guardrail 1: no address column on contacts).
 */

// ── the trigger ───────────────────────────────────────────────────────────────────────────────────

export function LinkedPersonCell({
  value,
  label,
  word,
  org,
  open,
  onOpen,
  extraNamed,
  testId = "interview-person",
}: {
  /** The stored value — a `{ display, ref }` string, or a legacy plain string, or empty. */
  value: string;
  /** What to show instead of the value (several names, the row's own name). */
  label?: string;
  /** The board cell's `word` (s228, C1): passed by every caller, never a default typed here (rule 137). */
  word: string;
  org?: boolean;
  open: boolean;
  onOpen: () => void;
  /** A second person named inside the card (the backup) — shown as a quiet "+ backup". */
  extraNamed?: string;
  testId?: string;
}) {
  const name = (label ?? formatContactSummary(value)).trim();
  const Mark = org ? Building2 : User;
  // s228 (D-227.25, look rows L20–L24) — the board's trigger, Main.dc.html:391-392 + `jointCell` :1422:
  //   `padding: 0 10px 0 12px; font-size: 14px` · svg `width="15"` `stroke="currentColor"` ·
  //   `fg: on ? '#2E4030' : '#4A4640'` · `boxBr: on ? '#4F6B51' : '#C4BCAA'`.
  // "On" is the board's ticked cell: its card is open, or it holds a name. The off border stays stone:
  // #C4BCAA is not a brand colour (rule 124 — reported s228, not typed in).
  const on = open || !!name;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-expanded={open}
      // s234 (D-234.77, refined for names by D-234.91): a long name WRAPS — up to THREE lines, then it is cut, and this
      // button opening the card is the way to read the rest (rule 133's s227 name exception, extended to the contact
      // button). The box grows from the board's 44px (h-auto + min-h-11 over CELL_BOX's fixed h-11). The marker lets
      // check:rule-133 credit a row for this button's own lines and nothing else.
      className={cn(CELL_BOX, "flex h-auto min-h-11 items-center gap-2 py-2 pl-3 pr-2.5 text-left text-[14px] hover:border-sage-600",
        on ? "border-sage-600 text-sage-800" : "text-warm-muted")}
      data-testid={testId}
      data-contact-trigger=""
    >
      <Mark className="h-[15px] w-[15px] shrink-0" aria-hidden="true" />
      <span className="line-clamp-3 min-w-0 break-words">{name || word}</span>
      {name && extraNamed && <span className="ml-auto shrink-0 text-[13px] text-warm-muted" data-testid={testId ? `${testId}-extra` : undefined}>+ {extraNamed}</span>}
    </button>
  );
}

// ── one linked (contact-typed) name ───────────────────────────────────────────────────────────────

/**
 * ContactInput is fully controlled, so this holds the draft it shows; it also defers its blur a tick
 * (the card/table blur race), so the commit reads the latest value from a ref, never a closure that
 * captured the value before the change landed. A typed name commits twice — `{ display }`, then
 * `{ display, ref }` once the mint returns — and `committed` lets the second, linked value through while
 * swallowing a repeat of the same one.
 */
function LinkedName({ value, onCommit, fieldId, clientId, testId, placeholder = LABELS.person.nP }: {
  value: string;
  onCommit: (value: string) => void;
  fieldId: string;
  clientId: string;
  testId?: string;
  /** s236 (D-236.71(5)): the card's own words — "The exact name" on a company card. */
  placeholder?: string;
}) {
  const [draft, setDraft] = useState(value);
  const latest = useRef(value);
  const committed = useRef(value);
  const commit = (v: string) => {
    if (v === committed.current) return;
    committed.current = v;
    onCommit(v);
  };
  return (
    <div data-testid={testId}>
      <ContactInput
        value={draft}
        onChange={(v) => { latest.current = v; setDraft(v); }}
        onBlur={() => commit(latest.current)}
        fieldId={fieldId}
        clientId={clientId}
        placeholder={placeholder}
        inCard
      />
    </div>
  );
}

// ── the card ──────────────────────────────────────────────────────────────────────────────────────

/** One person the card can hold, and the row fields their answers land in. */
export type CardSlot = {
  /** "Guardian", "Backup guardian", "Person 1", "The company", "A person to ask for". */
  label: string;
  /** The name: a `contact` field (a LINK), a `text` field (the words), or the row's own name parts. */
  name:
    | { kind: "contact"; key: string }
    | { kind: "text"; key: string }
    | { kind: "nameParts"; first: string; last: string };
  phone?: string;
  email?: string;
  address?: string;
  /** This slot is the company (org branch) — the board's company labels. */
  org?: boolean;
};

/** The name a slot holds right now, as words. Pure. */
export function slotName(slot: CardSlot, values: Readonly<Record<string, string>>): string {
  if (slot.name.kind === "nameParts") return composeContactName({ firstName: values[slot.name.first], surname: values[slot.name.last] });
  return formatContactSummary(values[slot.name.key] ?? "").trim();
}

/** The trigger's label: up to three names, then a count (Main.dc.html `personSummary`). Pure. */
export function cardSummary(slots: readonly CardSlot[], values: Readonly<Record<string, string>>): string {
  const names = slots.map((s) => slotName(s, values)).filter(Boolean);
  if (names.length <= 3) return names.join(", ");
  return `${names.slice(0, 3).join(", ")} and ${names.length - 3} more`;
}

const LABELS = {
  person: { n: "Name", nP: "First and last name", m: "Mobile", mP: "Best number", e: "Email", eP: "me@example.com" },
  org: { n: "Company name", nP: "The exact name", m: "Main phone", mP: "Main number", e: "Email or website", eP: "info@company.ca or company.ca" },
} as const;

// s228 (D-227.25, L2 + L3): the board's New-contact box, Main.dc.html:463 — `background: #FAF7F1` (cream) · `font-size: 14px`.
const FIELD = "h-11 w-full rounded-[8px] border border-warm-stone bg-warm-cream px-3 text-[14px] text-warm-charcoal placeholder:text-warm-muted focus:outline-none focus:border-sage-600";

export function ContactCard({
  title,
  slots,
  values,
  fieldIdByKey,
  clientId,
  onSave,
  onClose,
  undo,
  moreLabel,
  planPeople,
  onPickPlanPerson,
  linkedPerson = false,
  interviewLook = false,
  brands,
  addressOpenWhenEmpty = false,
  addressPlaceholder,
  addressAsLine = false,
  emptyWord,
  headerNote,
  extra,
  footerAction,
  testId = "interview-contact",
}: {
  title: string;
  /** 1–3 people, in order. The first is shown; the rest come in with the `more` link. */
  slots: readonly CardSlot[];
  /** The row's current values, by field key. */
  values: Readonly<Record<string, string>>;
  fieldIdByKey: ReadonlyMap<string, string>;
  clientId: string;
  /** Write the changed fields (field key → value) through the screen's one write path. Done WAITS for it: a
   *  refused save keeps the card open with what was typed (s228 INTERVIEW-SAVE-FAILURE, ruling 2). */
  onSave: (changes: Record<string, string>) => void | Promise<unknown>;
  onClose: () => void;
  /** The cell's `undo` word ("No guardian named") — the board's cancel: closes the card, writes nothing (H11). */
  undo?: string;
  moreLabel?: string;
  /**
   * s223 `fromKid` (D-222.10 part 4): people the plan ALREADY names who are not contacts — the planner's own
   * children from chapter one. The board: "on Beneficiaries the recent list can hold your own child from chapter
   * one". They sit in the Existing contact tab's Recent row; a pick hands the person to the screen (which carries
   * their answers in) and closes the card. Absent everywhere else, so every other card is unchanged.
   */
  planPeople?: readonly { id: string; label: string }[];
  onPickPlanPerson?: (id: string) => void;
  /**
   * s234 (D-233.72): the row IS a person already in the plan (a child's Beneficiaries row is the child) — the card
   * edits that person's own answers and never swaps in someone else, so there is no Existing contact tab.
   */
  linkedPerson?: boolean;
  /**
   * s235 (D-235.34): the card's open address in the board's look (`Main.dc.html:469-471` — the map pin, "Unit or suite",
   * the 2fr / 1fr then four-across rows; `AddressInput`'s `interviewLook="card"`). Default OFF: chapter two's cards
   * (`Ch2Engine.tsx`) and the plan home's (`HomeNeeds.tsx`) keep today's look until their own PR turns it on.
   */
  interviewLook?: boolean;
  /**
   * s237 (D-236.63(1); chapter three PR 2): the brand list a company card's name box offers — the board's chips under
   * "Company name" (Chapter3.dc.html `card.hasBrands`). A chip fills the name; the planner can still type any name.
   * Absent on every card whose board draws none (chapters one and two today), so those cards are unchanged.
   */
  brands?: readonly { label: string; mark?: string; color?: string }[];
  /**
   * s237 (Contacts PR E, D-237.68-3; the same per-card prop chapter three's employer card wants, D-237.21): the address box
   * shows OPEN even when empty — a box like the phone and the email, never folded behind "+ Add an address". Default OFF:
   * every other card keeps the fold (rule 63 as amended, D-236.71(4)).
   */
  addressOpenWhenEmpty?: boolean;
  /** s237 (D-237.68-3): the address box's prompt while empty (Contacts: board 12's "Street, city, postal code"). */
  addressPlaceholder?: string;
  /** s237 (Contacts PR E, D-237.68-3): the address as ONE box like the phone and the email — `AddressInput`'s "line" look (the
   *  interview's own address piece: one search line, the six parts open on Edit) — instead of the card's six boxes. */
  addressAsLine?: boolean;
  /** s237 (Contacts PR E, D-237.74): the word an EMPTY phone, email or address box reads — the executor's "Reade did not
   *  say", so they know to ask. Default: each box's own prompt. */
  emptyWord?: string;
  /** s237 Contacts PR F (item 146(3); board 12): a line in the card's HEADER, beside the title ("One record: a change here
   *  shows on every page … appears on"). Only a `linkedPerson` card has room for it — the others' header holds the tabs. */
  headerNote?: ReactNode;
  /** s237 Contacts PR F (item 146(1); board 12): drawn under the people, above Done (Contacts: a company's person to ask for). */
  extra?: ReactNode;
  /** s237 Contacts PR F (D-237.71; board 12): a control beside Done; the row then sits at the card's right, as drawn. */
  footerAction?: ReactNode;
  testId?: string;
}) {
  const people = slots.slice(0, 3);
  // Open with every person the row already holds — up to the LAST filled slot, so a named backup shows
  // even when the guardian above it is still empty.
  const lastFilled = people.reduce((last, s, i) => (slotName(s, values) ? i : last), -1);
  const [shown, setShown] = useState(Math.max(1, lastFilled + 1));
  const [tab, setTab] = useState<"new" | "existing">("new");
  const [draft, setDraft] = useState<Record<string, string>>({});
  // s228 (H9): an EMPTY address starts folded shut behind "+ Add an address" — the board's `a1Shut: !S.pick[key + ':addr1']`
  // (Main.dc.html:1423) opens only on a click. s236 (D-236.71(4), Reade: rule 63 becomes "folded shut when empty, open when
  // it holds an answer"): an address the row already holds opens with the card. Folding touches no value either way.
  const [openAddress, setOpenAddress] = useState<Record<number, boolean>>(() => {
    const open: Record<number, boolean> = {};
    people.forEach((s, i) => { if (s.address && (addressOpenWhenEmpty || formatAddressSummary(values[s.address]).trim() !== "")) open[i] = true; });
    return open;
  });
  // s236 (D-236.71(2); rule 159): a company card's person to ask for is the STANDARD person slot — its own New contact /
  // Existing contact. A slot's index → the search words while its Existing contact is open.
  const [slotExisting, setSlotExisting] = useState<Record<number, string>>({});
  const [query, setQuery] = useState("");
  // s237: a brand chip remounts the name box with the chip's words.
  const [nameBump, setNameBump] = useState(0);

  const current = (key: string) => (key in draft ? draft[key] : values[key] ?? "");
  const set = (key: string, v: string) => setDraft((d) => ({ ...d, [key]: v }));
  const nameFor = (s: CardSlot) =>
    s.name.kind === "nameParts" ? composeContactName({ firstName: current(s.name.first), surname: current(s.name.last) }) : current(s.name.key);
  const setName = (s: CardSlot, typed: string) => {
    if (s.name.kind !== "nameParts") return set(s.name.key, typed);
    const t = typed.trim().replace(/\s+/g, " ");
    const at = t.lastIndexOf(" ");
    set(s.name.first, at < 0 ? "" : t.slice(0, at));
    set(s.name.last, at < 0 ? t : t.slice(at + 1));
  };

  /** Every key a slot writes — used by Remove to clear what the person held. */
  const keysOf = (s: CardSlot) =>
    [...(s.name.kind === "nameParts" ? [s.name.first, s.name.last] : [s.name.key]), s.phone, s.email, s.address].filter((k): k is string => !!k);

  const { data: contacts = [] } = useQuery<ContactOption[]>({
    queryKey: [`/api/clients/${clientId}/contacts`],
    staleTime: Infinity,
    retry: false,
    enabled: !!clientId,
  });
  const matches = useMemo(() => matchContacts(query, contacts ?? []), [query, contacts]);

  // ── the contact record gets the mobile and email too (CONTACT-CARD-ONE-1) ──
  const queryClient = useQueryClient();
  const patchContact = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: { phone?: string; email?: string } }) => {
      await apiRequest("PATCH", `/api/clients/${clientId}/contacts/${id}`, body);
    },
  });
  // s236 (D-236.50-24; Window 3's review §3): a company named in TEXT (a school, a charity, a lender) is no longer minted
  // into the legacy `contacts` table here — the row's own save makes it a person (kind organisation) with its phone, email
  // and address on that one record (pilot-map's `org` links). Only a contact-linked name's reach is carried below.
  const reachOf = (s: CardSlot, read: (k: string) => string) => {
    const body: { phone?: string; email?: string } = {};
    const phone = s.phone ? read(s.phone).trim() : "";
    const email = s.email ? read(s.email).trim() : "";
    if (phone) body.phone = phone;
    if (email) body.email = email;
    return body;
  };
  /** Carry each changed slot's mobile and email onto its contact record. The row write has already
   *  happened; a failure here leaves the row answer standing and the contact as it was. */
  const syncContacts = async (changed: Record<string, string>, read: (k: string) => string) => {
    for (const s of people.slice(0, shown)) {
      if (!keysOf(s).some((k) => k in changed)) continue;
      const body = reachOf(s, read);
      if (s.name.kind === "contact") {
        const ref = parseContact(read(s.name.key))?.ref;
        if (ref && Object.keys(body).length) await patchContact.mutateAsync({ id: ref, body });
      }
    }
    queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/contacts`] });
  };
  const recent = (contacts ?? []).slice(-3).reverse();

  /**
   * s227 (D11, Reade: "pick several existing contacts") — the board's Existing contact is a set of TICKS, not one
   * pick (Main.dc.html:549, each Recent pill calls `planTick`, :1371-1377: it toggles a tick and the tick COUNT
   * sets how many people the card holds; the card stays open). Here: each pick toggles; the ticked contacts fill
   * the slots in the order they were ticked (the link, phone and email, as one pick did before); unticking puts
   * that slot back to what the row already held. A one-person card swaps its person instead of refusing.
   */
  const [picked, setPicked] = useState<ContactOption[]>([]);
  const applyPicks = (list: readonly ContactOption[], before: readonly ContactOption[]) => {
    const next: Record<string, string> = {};
    people.forEach((s, i) => {
      const c = list[i];
      if (c) {
        const display = composeContactName(c);
        if (s.name.kind === "contact") next[s.name.key] = toContactValue(display, c.id);
        else if (s.name.kind === "nameParts") {
          const t = display.trim().replace(/\s+/g, " ");
          const at = t.lastIndexOf(" ");
          next[s.name.first] = at < 0 ? "" : t.slice(0, at);
          next[s.name.last] = at < 0 ? t : t.slice(at + 1);
        } else next[s.name.key] = display;
        if (s.phone && c.phone) next[s.phone] = c.phone;
        if (s.email && c.email) next[s.email] = c.email;
      } else if (before[i]) {
        // This slot held a pick that is now unticked: back to what the row already held.
        for (const k of keysOf(s)) next[k] = values[k] ?? "";
      }
    });
    setDraft((d) => ({ ...d, ...next }));
    setShown((n) => Math.max(n, list.length));
  };
  const pickExisting = (c: ContactOption) => {
    const on = picked.some((p) => p.id === c.id);
    let list: ContactOption[];
    if (on) list = picked.filter((p) => p.id !== c.id);
    else if (people.length === 1) list = [c];
    else if (picked.length >= people.length) return; // every slot is taken: untick someone first
    else list = [...picked, c];
    applyPicks(list, picked);
    setPicked(list);
  };
  const isPicked = (c: ContactOption) => picked.some((p) => p.id === c.id);

  const save = () => {
    const changes: Record<string, string> = {};
    for (const [k, v] of Object.entries(draft)) if (v !== (values[k] ?? "")) changes[k] = v;
    if (!Object.keys(changes).length) return onClose();
    // s228 (INTERVIEW-SAVE-FAILURE, ruling 2): the card closes only once the row's save has landed. Refused → the
    // pop-up has said so and the card stays open with what was typed; the contact copy waits for the row.
    whenSaved(onSave(changes), () => {
      const snapshot = { ...values, ...draft };
      void syncContacts(changes, (k) => snapshot[k] ?? "").catch(() => {});
      onClose();
    });
  };

  /**
   * s228 (Reade: "Remove removes THE PERSON IT SITS BESIDE — never the last person"; reverses the board-matching H7 of
   * 49dc6844, whose `drop` took the last one). The people after it move up one slot, each keeping every answer they
   * held; the last slot is cleared. Nothing is written until Done. The person blocks remount (`generation`) because
   * their boxes hold their own drafts and would otherwise keep showing the old values.
   */
  const [generation, setGeneration] = useState(0);
  const moveInto = (to: CardSlot, from: CardSlot) => {
    if (to.name.kind === "nameParts" && from.name.kind === "nameParts") {
      set(to.name.first, current(from.name.first));
      set(to.name.last, current(from.name.last));
    } else if (to.name.kind !== "nameParts" && from.name.kind === to.name.kind) {
      set(to.name.key, current((from.name as { key: string }).key));
    } else {
      // Different kinds of name slot: the words carry over (a link to a contact cannot become name parts).
      const words = from.name.kind === "nameParts" ? nameFor(from) : formatContactSummary(current(from.name.key));
      setName(to, words);
    }
    for (const r of ["phone", "email", "address"] as const) if (to[r]) set(to[r]!, from[r] ? current(from[r]!) : "");
  };
  const remove = (i: number) => {
    if (i === 0 || i >= shown) return;
    for (let j = i; j < shown - 1; j++) moveInto(people[j], people[j + 1]);
    for (const k of keysOf(people[shown - 1])) set(k, "");
    setOpenAddress((o) => {
      const n: Record<number, boolean> = {};
      for (const [k, v] of Object.entries(o)) { const x = Number(k); if (x < i) n[x] = v; else if (x > i) n[x - 1] = v; }
      return n;
    });
    setPicked((p) => p.filter((_, j) => j !== i));
    setShown(shown - 1);
    setGeneration((g) => g + 1);
  };

  /** s236 (D-236.71(2)): pick an EXISTING person for one slot — the link to that one record (rule 158), never a copy. */
  const pickForSlot = (s: CardSlot, i: number, c: ContactOption) => {
    if (s.name.kind !== "contact") return;
    set(s.name.key, toContactValue(composeContactName(c), c.id));
    if (s.phone && c.phone) set(s.phone, c.phone);
    if (s.email && c.email) set(s.email, c.email);
    setSlotExisting((x) => { const n = { ...x }; delete n[i]; return n; });
    setGeneration((g) => g + 1);
  };

  const person = (s: CardSlot, i: number) => {
    const L = s.org ? LABELS.org : LABELS.person;
    const tid = `${testId}-p${i + 1}`;
    // s236 (D-236.71(2)): the person to ask for on a company card — a later, person slot under an org first slot.
    const askFor = i > 0 && !!people[0]?.org && !s.org && s.name.kind === "contact" && !linkedPerson;
    const slotQuery = slotExisting[i];
    const slotPicking = askFor && slotQuery !== undefined;
    const slotPeople = (contacts ?? []).filter((c) => c.kind !== "organisation");
    const slotMatches = slotPicking ? (slotQuery.trim() ? matchContacts(slotQuery, slotPeople) : slotPeople.slice(-3).reverse()) : [];
    const nameFieldId = s.name.kind === "contact" ? fieldIdByKey.get(s.name.key) : undefined;
    /**
     * s224 fix 1 — the name is TWO labelled inputs, not one blob a planner has to guess the shape of.
     *
     * 🔴 It splits only where the row ALREADY stores two fields (`nameParts`). The other two kinds do
     * not, and neither is an oversight:
     *   • `org` — a company has one name. Offering to split it into a given name and a family name is
     *     the exact mistake the s205 mint fix removed (`components/__tests__/ContactCard.test.tsx`).
     *   • `text` on a person (exec's `name`) — ONE row field, and `legal-your-executor.name` is read as
     *     one field by the `executor_identified` essential, a task binding and the executor invite.
     *     Splitting it is a data act plus a reader sweep, so it is REPORTED, not done quietly here.
     *   • `contact` — the name is a LINK, owned by `ContactInput` (search, match, mint). Its own card
     *     already offers "Split into first + last" one level down. Replacing it with two plain inputs
     *     would drop the linking, so that one is reported too rather than broken.
     */
    const splitName = s.name.kind === "nameParts" && !s.org;
    return (
      // s228 (D-227.25, L5): every part of a person sits in the card's one column, `gap: 14px` (Main.dc.html:452).
      <div key={`${i}-${generation}`} className="flex flex-col gap-3.5" data-testid={tid}>
        {/* s228 (H6): the person's label shows only when the card holds more than one person — the board's
            `many: ps.n > 1` (Main.dc.html:1386, used at :459); a later person's header always shows (:481). */}
        {shown > 1 && (
          // L6: a later person's header carries the board's rule — `border-top: 1px solid #ECE7DC; padding-top: 10px`,
          // baseline-aligned, 12px apart (Main.dc.html:481).
          <div className={`flex items-baseline justify-between gap-3${i > 0 ? " border-t border-warm-stone-light pt-2.5" : ""}`} data-testid={`${tid}-header`}>
            <span className="text-[13px] uppercase tracking-[0.06em] text-warm-muted">{s.label}</span>
            {/* s228 (H7): Remove on EVERY later person's header, as the board draws it (:483, :505). Reade (s228):
                it removes the person it sits beside, never the last one — see `remove`. */}
            {i > 0 && (
              <button type="button" onClick={() => remove(i)} className="text-[13px] text-warm-muted hover:text-warm-charcoal" data-testid={`${tid}-remove`}>
                Remove
              </button>
            )}
          </div>
        )}
        {askFor && (
          <div className="flex self-start overflow-hidden rounded-[8px] border border-warm-stone" role="tablist" data-testid={`${tid}-tabs`}>
            <button type="button" role="tab" aria-selected={!slotPicking} onClick={() => setSlotExisting((x) => { const n = { ...x }; delete n[i]; return n; })}
              className={tabClass(!slotPicking)} data-testid={`${tid}-tab-new`}>New contact</button>
            <button type="button" role="tab" aria-selected={slotPicking} onClick={() => setSlotExisting((x) => ({ ...x, [i]: x[i] ?? "" }))}
              className={`${tabClass(slotPicking)} border-l border-warm-stone`} data-testid={`${tid}-tab-existing`}>Existing contact</button>
          </div>
        )}
        {slotPicking && (
          <div className="flex flex-col gap-2.5" data-testid={`${tid}-existing`}>
            <div className="relative max-w-[420px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-warm-taupe" aria-hidden="true" />
              <input className={cn(FIELD, "bg-warm-white pl-9")} placeholder="Search your contacts — start typing a name" value={slotQuery}
                onChange={(e) => { const v = e.target.value; setSlotExisting((x) => ({ ...x, [i]: v })); }} data-testid={`${tid}-search`} />
            </div>
            {slotMatches.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {slotMatches.map((c) => (
                  <button key={c.id} type="button" onClick={() => pickForSlot(s, i, c)}
                    className="min-h-11 rounded-full border border-warm-stone bg-warm-white px-3.5 text-[13px] text-warm-charcoal hover:border-sage-600"
                    data-testid={`${tid}-pick-${c.id}`}>
                    {composeContactName(c)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {!slotPicking && splitName && s.name.kind === "nameParts" && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid={`${tid}-name-fields`}>
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] text-warm-muted">First name</span>
              <input className={FIELD} placeholder="Given name, and any middle name" autoComplete="off"
                aria-label="First name" title={current(s.name.first)} defaultValue={current(s.name.first)}
                onChange={(e) => { set((s.name as { first: string }).first, e.target.value); e.target.title = e.target.value; }}
                data-testid={`${tid}-first`} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] text-warm-muted">Last name</span>
              <input className={FIELD} placeholder="Family name" autoComplete="off"
                aria-label="Last name" title={current(s.name.last)} defaultValue={current(s.name.last)}
                onChange={(e) => { set((s.name as { last: string }).last, e.target.value); e.target.title = e.target.value; }}
                data-testid={`${tid}-last`} />
            </label>
          </div>
        )}
        {!slotPicking && (<>
        <div className={`grid grid-cols-1 gap-3 ${splitName ? "sm:grid-cols-2" : "sm:[grid-template-columns:minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1.3fr)]"}`} data-testid={`${tid}-fields`}>
          {!splitName && (
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] text-warm-muted">{L.n}</span>
            {s.name.kind === "contact" && nameFieldId ? (
              <LinkedName
                value={current(s.name.key)}
                fieldId={nameFieldId}
                clientId={clientId}
                // A linked name commits the moment it resolves (the mint is async); Done saves the rest.
                onCommit={(v) => {
                  const key = (s.name as { key: string }).key;
                  set(key, v);
                  saveInBackground(onSave({ [key]: v }));
                  // The mint can resolve AFTER Done — carry the typed mobile and email onto it then.
                  const ref = parseContact(v)?.ref;
                  const body = reachOf(s, current);
                  if (ref && Object.keys(body).length) void patchContact.mutateAsync({ id: ref, body }).catch(() => {});
                }}
                testId={`${tid}-name`}
                placeholder={L.nP}
              />
            ) : (
              <input key={`name-${nameBump}`} className={FIELD} placeholder={L.nP} defaultValue={nameFor(s)} autoComplete="off"
                title={nameFor(s)}
                onChange={(e) => { setName(s, e.target.value); e.target.title = e.target.value; }} data-testid={`${tid}-name`} />
            )}
            {s.org && i === 0 && brands && brands.length > 0 && (
              // s237 (D-236.63(1)): the board's brand chips under the company name — a chip fills the name.
              <div className="flex flex-wrap gap-1.5 pt-0.5" data-testid={`${tid}-brands`}>
                {brands.map((b) => (
                  <button key={b.label} type="button" onClick={() => { setName(s, b.label); setNameBump((n) => n + 1); }}
                    className="flex min-h-11 items-center gap-1.5 rounded-[8px] border border-warm-stone bg-warm-white py-[3px] pl-1 pr-2.5 text-[13px] text-warm-charcoal hover:border-sage-600"
                    data-testid={`${tid}-brand`}>
                    {b.mark && (
                      <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[6px] text-[8px] font-medium text-white"
                        style={{ backgroundColor: b.color }} aria-hidden="true">{b.mark}</span>
                    )}
                    <span>{b.label}</span>
                  </button>
                ))}
              </div>
            )}
          </label>
          )}
          {s.phone && (
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] text-warm-muted">{L.m}</span>
              {/* 🔴 s224 fix 2 — the product's ONE phone convention, which THIS box was ignoring while
                  the classic contact card (`ContactInput.tsx:448`) already followed it. Same helper,
                  same blur moment, same behaviour — including leaving an international number and any
                  non-standard length UNTOUCHED, which `shared/phone.ts` states is the design.
                  A second convention is NOT invented here; see the report for the format question. */}
              <input className={FIELD} placeholder={emptyWord ?? L.mP} type="tel" inputMode="tel" autoComplete="off"
                title={current(s.phone)} defaultValue={current(s.phone)}
                onChange={(e) => { set(s.phone!, e.target.value); e.target.title = e.target.value; }}
                onBlur={(e) => { const f = formatPhone(e.target.value); e.target.value = f; e.target.title = f; set(s.phone!, f); }}
                data-testid={`${tid}-phone`} />
            </label>
          )}
          {s.email && (
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] text-warm-muted">{L.e}</span>
              {/* 🔴 s224 fix 3 — Chrome offered the PLANNER'S OWN address into a contact card for
                  someone else (Reade, G4.1 walk). Email is the field browser identity-autofill cares
                  about; `autocomplete="off"` is what stops it. Applied to every input in this card,
                  not just this one, because the same class reaches name and phone. */}
              <input className={FIELD} placeholder={emptyWord ?? L.eP} inputMode="email" autoComplete="off"
                title={current(s.email)} defaultValue={current(s.email)}
                onChange={(e) => { set(s.email!, e.target.value); e.target.title = e.target.value; }} data-testid={`${tid}-email`} />
              {/* s236 (EMAIL-FORMAT-1, D-236.38(3)): a quiet line, never a block; a company card also takes a web address. */}
              <EmailHint value={current(s.email)} allowWebsite={!!s.org} className="text-[12px] text-warm-muted" testId={`${tid}-email-hint`} />
            </label>
          )}
        </div>
        {s.address && fieldIdByKey.get(s.address) && (
          openAddress[i] ? (
            // s228 (D-227.25, L7 + L8): the board's open address is a plain column, `gap: 10px`, no border (Main.dc.html:474);
            // the label and its hint are two spans 10px apart (:475).
            <div className="flex flex-col gap-2.5" data-testid={`${tid}-address`}>
              <div className="flex items-baseline justify-between gap-3">
                <div className="flex items-baseline gap-2.5">
                  <span className="text-[13px] text-warm-muted">Address</span>
                  <span className="text-[13px] text-warm-muted">start typing and pick it from the list — the rest fills itself in</span>
                </div>
                {/* s227 (D11, Reade: "Remove address keeps the typed address"): the board's `a1Hide` (Main.dc.html:475,
                    `setIn('pick', key + ':addr1', …)`) only FOLDS the block shut — it touches no value. This button used to
                    write "" as well, so folding it away wiped the address on Done. */}
                {/* s237 Contacts PR F (item 146(2)): a box that is open while empty has nothing to remove — the word shows only
                    once there is an address. Every other card opens the box only on "+ Add an address", so it keeps its fold. */}
                {(!addressOpenWhenEmpty || formatAddressSummary(current(s.address)).trim() !== "") && (
                <button type="button" onClick={() => setOpenAddress((o) => ({ ...o, [i]: false }))}
                  className="shrink-0 text-[13px] text-warm-muted hover:text-warm-charcoal" data-testid={`${tid}-address-remove`}>
                  Remove address
                </button>
                )}
              </div>
              <AddressInput value={current(s.address)} onChange={(v) => set(s.address!, v)} onBlur={() => {}} fieldId={fieldIdByKey.get(s.address)!}
                interviewLook={addressAsLine ? "line" : interviewLook ? "card" : undefined} linePlaceholder={emptyWord ?? addressPlaceholder} lineScrolls={addressAsLine} />
            </div>
          ) : (
            <button type="button" onClick={() => setOpenAddress((o) => ({ ...o, [i]: true }))}
              // s228 (D-227.25, L10): `min-height: 32px` (Main.dc.html:471).
              className="flex min-h-8 items-center self-start text-[13px] text-sage-600 hover:text-sage-800" data-testid={`${tid}-address-add`}>
              + Add an address
            </button>
          )
        )}
        </>)}
      </div>
    );
  };

  const tabClass = (on: boolean) =>
    "h-11 whitespace-nowrap px-3.5 text-[13px] " + (on ? "bg-sage-50 text-sage-800" : "bg-warm-white text-warm-muted hover:text-warm-charcoal");

  return (
    // s221 Lane 1c gate (Reade): the board's card, not a full-width panel — Main.dc.html "THE CONTACT CARD":
    // margin 2px 2px 12px 38px · max-width 760px · 1px sage border · radius 12px · padding 16px 18px · 14px gap.
    <div className="mb-3 ml-[38px] mr-0.5 mt-0.5 flex max-w-[760px] flex-col gap-3.5 rounded-[12px] border border-sage-600 bg-warm-white px-[18px] py-4"
      data-testid={`${testId}-card`} {...(addressOpenWhenEmpty ? { "data-address-open": "D-237.21" } : {})}>
      {/* s228 (D-227.25, L18): the board's header row does not wrap (Main.dc.html:453); below `sm` it still may —
          the product's phone rule, one of the nine deliberate differences (L19). */}
      <div className="flex flex-wrap items-baseline justify-between gap-3 sm:flex-nowrap">
        <span className="text-[14px] text-warm-charcoal">{title}</span>
        {linkedPerson && headerNote && <span className="text-[13px] text-warm-muted">{headerNote}</span>}
        {!linkedPerson && (
        <div className="flex overflow-hidden rounded-[8px] border border-warm-stone" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "new"} onClick={() => setTab("new")} className={tabClass(tab === "new")} data-testid={`${testId}-tab-new`}>
            New contact
          </button>
          <button type="button" role="tab" aria-selected={tab === "existing"} onClick={() => setTab("existing")} className={`${tabClass(tab === "existing")} border-l border-warm-stone`} data-testid={`${testId}-tab-existing`}>
            Existing contact
          </button>
        </div>
        )}
      </div>

      {tab === "new" ? (
        <div className="flex flex-col gap-3.5">{people.slice(0, shown).map(person)}</div>
      ) : (
        <div className="flex flex-col gap-2.5" data-testid={`${testId}-existing`}>
          {/* s228 (D-227.25, L11 + L12): the board's search box, Main.dc.html:547 — `max-width: 420px`, `background: #FEFCF8`,
              its glass `width="15"` in #9B9383. */}
          <div className="relative max-w-[420px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-warm-taupe" aria-hidden="true" />
            <input className={cn(FIELD, "bg-warm-white pl-9")} placeholder="Search your contacts — start typing a name" value={query}
              onChange={(e) => setQuery(e.target.value)} data-testid={`${testId}-search`} />
          </div>
          {matches.length > 0 && (
            <div className="flex flex-col overflow-hidden rounded-[8px] border border-warm-stone">
              {matches.map((c) => (
                <button key={c.id} type="button" onClick={() => pickExisting(c)} aria-pressed={isPicked(c)}
                  className={`px-3 py-2.5 text-left text-[14px] ${isPicked(c) ? "bg-sage-50 text-sage-800" : "hover:bg-warm-stone-light"}`}
                  data-testid={`${testId}-match-${c.id}`}>
                  {composeContactName(c)}
                </button>
              ))}
            </div>
          )}
          {(recent.length > 0 || (planPeople?.length ?? 0) > 0) && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[13px] text-warm-muted">Recent</span>
              {(planPeople ?? []).map((p) => (
                <button key={`plan-${p.id}`} type="button" onClick={() => { onPickPlanPerson?.(p.id); onClose(); }}
                  className="min-h-11 rounded-full border border-warm-stone bg-warm-white px-3.5 text-[13px] text-warm-charcoal hover:border-sage-600"
                  data-testid={`${testId}-plan-${p.id}`}>
                  {p.label}
                </button>
              ))}
              {recent.map((c) => (
                // The board's ticked pill (Main.dc.html:549, planSty): sage border and fill when ticked; s228 (L13):
                // `padding: 0 14px; min-height: 44px`.
                <button key={c.id} type="button" onClick={() => pickExisting(c)} aria-pressed={isPicked(c)}
                  className={`min-h-11 rounded-full border px-3.5 text-[13px] ${isPicked(c) ? "border-sage-600 bg-sage-50 text-sage-800" : "border-warm-stone bg-warm-white text-warm-charcoal hover:border-sage-600"}`}
                  data-testid={`${testId}-recent-${c.id}`}>
                  {composeContactName(c)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "new" && extra}

      <div className={`flex flex-wrap items-center gap-4${footerAction ? " justify-end gap-3" : ""}`}>
        {footerAction}
        {tab === "new" && shown < people.length && moreLabel && (
          // s228 (D-227.25, L16 + L17): `font-size: 13px; margin-right: 4px` (Main.dc.html:555).
          <button type="button" onClick={() => setShown(shown + 1)} className="mr-1 text-[13px] text-sage-600 hover:text-sage-800" data-testid={`${testId}-more`}>
            {moreLabel}
          </button>
        )}
        {/* s228 (D-227.25, L15): Done is `padding: 0 18px; font-size: 13px` (Main.dc.html:556). */}
        <button type="button" onClick={save} className="h-11 rounded-[8px] bg-sage-600 px-[18px] text-[13px] text-warm-white hover:bg-sage-800" data-testid={`${testId}-done`}>
          Done
        </button>
        {undo && (
          // s228 (H11, Reade: "Cancel must never blank answers already saved"): the board's cancel (Main.dc.html:1420,
          // `jointCell`'s onCancel only unticks; :1431 `benefCell`'s only resets the pick — "neither deletes answers")
          // closes the card and drops what was typed here. It writes NOTHING. Clearing a saved answer is done by
          // emptying its boxes and pressing Done.
          <button type="button" onClick={onClose}
            // s228 (D-227.25, L16): the cancel word is 13px (Main.dc.html:557).
            className="text-[13px] text-warm-muted hover:text-warm-charcoal" data-testid={`${testId}-undo`}>
            {undo}
          </button>
        )}
      </div>
    </div>
  );
}
